import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Select,
  message,
  Typography,
  Row,
  Col,
  Progress,
  Alert,
  Spin,
  Divider,
  Tag,
  Statistic
} from 'antd';
import {
  RobotOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  InfoCircleOutlined,
  CopyOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const AITools = () => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [generatedContent, setGeneratedContent] = useState('');
  const [copywritingForm] = Form.useForm();
  const [headlineForm] = Form.useForm();

  useEffect(() => {
    fetchUsageData();
  }, []);

  const fetchUsageData = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/agency/ai-tools/usage');
      setUsageData(response.data);
    } catch (error) {
      console.error('Error fetching usage data:', error);
      message.error('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  const generateAdCopy = async (values) => {
    if (!checkUsageLimit()) return;

    setGenerating(true);
    try {
      const response = await axios.post('/api/agency/ai-tools/generate-ad-copy', values);
      setGeneratedContent(response.data.content);
      message.success('Ad copy generated successfully');
      fetchUsageData(); // Refresh usage
    } catch (error) {
      console.error('Error generating ad copy:', error);
      message.error(error.response?.data?.message || 'Failed to generate ad copy');
    } finally {
      setGenerating(false);
    }
  };

  const generateHeadlines = async (values) => {
    if (!checkUsageLimit()) return;

    setGenerating(true);
    try {
      const response = await axios.post('/api/agency/ai-tools/generate-headlines', values);
      setGeneratedContent(response.data.content);
      message.success('Headlines generated successfully');
      fetchUsageData();
    } catch (error) {
      console.error('Error generating headlines:', error);
      message.error(error.response?.data?.message || 'Failed to generate headlines');
    } finally {
      setGenerating(false);
    }
  };

  const checkUsageLimit = () => {
    if (usageData && usageData.used >= usageData.limit) {
      message.warning('You have reached your monthly AI usage limit. Please upgrade your plan or wait until next month.');
      return false;
    }
    return true;
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedContent);
    message.success('Copied to clipboard');
  };

  const getUsagePercentage = () => {
    if (!usageData) return 0;
    return Math.round((usageData.used / usageData.limit) * 100);
  };

  const getUsageStatus = () => {
    const percentage = getUsagePercentage();
    if (percentage >= 90) return 'exception';
    if (percentage >= 70) return 'normal';
    return 'active';
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <RobotOutlined /> AI Tools
      </Title>
      <Paragraph type="secondary">
        Generate high-quality ad copy, headlines, and content using AI. Usage is limited per your corporate plan.
      </Paragraph>

      {/* Usage Statistics */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="AI Generations Used"
              value={usageData?.used || 0}
              suffix={`/ ${usageData?.limit || 0}`}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Remaining"
              value={usageData ? usageData.limit - usageData.used : 0}
              valueStyle={{ color: getUsagePercentage() >= 90 ? '#cf1322' : '#3f8600' }}
            />
          </Col>
          <Col span={8}>
            <div>
              <Text type="secondary">Usage Progress</Text>
              <Progress
                percent={getUsagePercentage()}
                status={getUsageStatus()}
                style={{ marginTop: 8 }}
              />
            </div>
          </Col>
        </Row>

        {usageData && getUsagePercentage() >= 80 && (
          <Alert
            message="Usage Limit Warning"
            description={`You have used ${getUsagePercentage()}% of your monthly AI generation limit. Consider upgrading your plan for more usage.`}
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

        {usageData && usageData.resetDate && (
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">
              Usage resets on: <Tag color="blue">{new Date(usageData.resetDate).toLocaleDateString()}</Tag>
            </Text>
          </div>
        )}
      </Card>

      <Tabs
        defaultActiveKey="ad-copy"
        items={[
          {
            key: 'ad-copy',
            label: (
              <span>
                <FileTextOutlined /> Ad Copywriting
              </span>
            ),
            children: (
              <Card>
                <Alert
                  message="AI-Powered Ad Copy Generation"
                  description="Generate compelling ad copy tailored to your target audience and campaign goals."
                  type="info"
                  icon={<InfoCircleOutlined />}
                  showIcon
                  style={{ marginBottom: 24 }}
                />

                <Form
                  form={copywritingForm}
                  layout="vertical"
                  onFinish={generateAdCopy}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Product/Service"
                        name="product"
                        rules={[{ required: true, message: 'Please enter product/service' }]}
                      >
                        <Input placeholder="e.g., Premium Coffee Beans" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Target Audience"
                        name="audience"
                        rules={[{ required: true, message: 'Please enter target audience' }]}
                      >
                        <Input placeholder="e.g., Coffee enthusiasts aged 25-45" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Tone"
                        name="tone"
                        rules={[{ required: true, message: 'Please select tone' }]}
                      >
                        <Select placeholder="Select tone">
                          <Option value="professional">Professional</Option>
                          <Option value="casual">Casual</Option>
                          <Option value="friendly">Friendly</Option>
                          <Option value="urgent">Urgent</Option>
                          <Option value="luxury">Luxury</Option>
                          <Option value="playful">Playful</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Ad Length"
                        name="length"
                        rules={[{ required: true, message: 'Please select length' }]}
                      >
                        <Select placeholder="Select length">
                          <Option value="short">Short (50-100 characters)</Option>
                          <Option value="medium">Medium (100-200 characters)</Option>
                          <Option value="long">Long (200-300 characters)</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Key Features/Benefits"
                    name="features"
                    rules={[{ required: true, message: 'Please enter key features' }]}
                  >
                    <TextArea
                      rows={3}
                      placeholder="e.g., Organic, Fair Trade, Rich Flavor, Ethically Sourced"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Call to Action"
                    name="cta"
                  >
                    <Input placeholder="e.g., Shop Now, Learn More, Get Started" />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<ThunderboltOutlined />}
                      loading={generating}
                      size="large"
                      disabled={usageData && usageData.used >= usageData.limit}
                    >
                      Generate Ad Copy
                    </Button>
                  </Form.Item>
                </Form>

                {generatedContent && (
                  <>
                    <Divider />
                    <Card
                      title="Generated Ad Copy"
                      extra={
                        <Button
                          icon={<CopyOutlined />}
                          onClick={copyToClipboard}
                        >
                          Copy
                        </Button>
                      }
                    >
                      <Paragraph style={{ fontSize: 16, whiteSpace: 'pre-wrap' }}>
                        {generatedContent}
                      </Paragraph>
                    </Card>
                  </>
                )}
              </Card>
            )
          },
          {
            key: 'headlines',
            label: (
              <span>
                <FileTextOutlined /> Headline Generator
              </span>
            ),
            children: (
              <Card>
                <Alert
                  message="AI-Powered Headline Generation"
                  description="Generate multiple attention-grabbing headlines for your campaigns."
                  type="info"
                  icon={<InfoCircleOutlined />}
                  showIcon
                  style={{ marginBottom: 24 }}
                />

                <Form
                  form={headlineForm}
                  layout="vertical"
                  onFinish={generateHeadlines}
                >
                  <Form.Item
                    label="Topic/Product"
                    name="topic"
                    rules={[{ required: true, message: 'Please enter topic' }]}
                  >
                    <Input placeholder="e.g., Summer Sale on Outdoor Furniture" />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Style"
                        name="style"
                        rules={[{ required: true, message: 'Please select style' }]}
                      >
                        <Select placeholder="Select style">
                          <Option value="question">Question-Based</Option>
                          <Option value="benefit">Benefit-Focused</Option>
                          <Option value="urgency">Urgency-Driven</Option>
                          <Option value="curiosity">Curiosity-Inducing</Option>
                          <Option value="direct">Direct & Clear</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Number of Headlines"
                        name="count"
                        rules={[{ required: true, message: 'Please select count' }]}
                      >
                        <Select placeholder="Select count">
                          <Option value="3">3 Headlines</Option>
                          <Option value="5">5 Headlines</Option>
                          <Option value="10">10 Headlines</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Keywords (Optional)"
                    name="keywords"
                  >
                    <Input placeholder="e.g., discount, limited time, exclusive" />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<ThunderboltOutlined />}
                      loading={generating}
                      size="large"
                      disabled={usageData && usageData.used >= usageData.limit}
                    >
                      Generate Headlines
                    </Button>
                  </Form.Item>
                </Form>

                {generatedContent && (
                  <>
                    <Divider />
                    <Card
                      title="Generated Headlines"
                      extra={
                        <Button
                          icon={<CopyOutlined />}
                          onClick={copyToClipboard}
                        >
                          Copy All
                        </Button>
                      }
                    >
                      <Paragraph style={{ fontSize: 16, whiteSpace: 'pre-wrap' }}>
                        {generatedContent}
                      </Paragraph>
                    </Card>
                  </>
                )}
              </Card>
            )
          },
          {
            key: 'usage-history',
            label: (
              <span>
                <InfoCircleOutlined /> Usage History
              </span>
            ),
            children: (
              <Card>
                <Title level={4}>AI Tools Usage History</Title>
                <Paragraph type="secondary">
                  Track your AI generation usage across all locations in your enterprise account.
                </Paragraph>

                <Row gutter={16} style={{ marginTop: 24 }}>
                  <Col span={8}>
                    <Card>
                      <Statistic
                        title="This Month"
                        value={usageData?.used || 0}
                        suffix="generations"
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card>
                      <Statistic
                        title="Plan Limit"
                        value={usageData?.limit || 0}
                        suffix="generations"
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card>
                      <Statistic
                        title="Plan Type"
                        value={usageData?.planType || 'Enterprise'}
                        valueStyle={{ fontSize: 20 }}
                      />
                    </Card>
                  </Col>
                </Row>

                <Alert
                  message="Corporate Plan Limits"
                  description="AI tool usage is limited based on your corporate plan. All locations share the same usage pool. Contact support to upgrade your plan for higher limits."
                  type="info"
                  showIcon
                  style={{ marginTop: 24 }}
                />
              </Card>
            )
          }
        ]}
      />
    </div>
  );
};

export default AITools;
